import React from "react";
import Gallery from "./Gallery.jsx";
import {
  Building2,
  ShieldCheck,
  BadgeCheck,
  FileText,
  Users,
  Handshake,
  CheckCircle2,
} from "lucide-react";
import SEO from "../Components/SEO";


const About = () => {
  const features = [
    "100% Industrial Supply Network",
    "Proper GST Registration & Verified Warehouse Address",
    "Complete GST Compliance",
    "Timely GST Return Filing & Tax Payments",
    "Ethical & Responsible Business Practices",
    "Strong Reputation in the Industry",
    "Long-Term & Reliable Business Relationships",
  ];

  return (
    <>
      <SEO 
        title="About Us | Jaina Enterprises - Scrap Metal Experts" 
        description="Learn more about Jaina Enterprises, our industrial supply network, our verified warehouse, and our commitment to complete GST compliance and ethical recycling practices."
        keywords="about Jaina Enterprises, scrap metal experts, industrial scrap recycling, GST registered scrap dealer, scrap metal warehouse"
      />
     <style>{`
*{
  margin:0;
  padding:0;
  box-sizing:border-box;
}

.about-section{
  padding:100px 20px;
  background:linear-gradient(135deg,#f8faff,#eef3ff);
  font-family:'Poppins',sans-serif;
  overflow:hidden;
}

.about-container{
  max-width:1300px;
  margin:auto;
}

/* =========================
   HEADER
========================= */

.about-header{
  text-align:center;
  margin-bottom:70px;
}

.about-icon{
  color:#0B2E7A;
  animation:floatIcon 3s ease-in-out infinite;
}

@keyframes floatIcon{

  0%{
    transform:translateY(0);
  }

  50%{
    transform:translateY(-12px);
  }

  100%{
    transform:translateY(0);
  }

}

.about-title{
  font-size:48px;
  color:#0B2E7A;
  font-weight:700;
  margin-top:15px;
  display:inline-block;
  cursor:pointer;
  transition:.35s;
}

.about-title:hover{
  transform:scale(1.05);
  color:#1748b5;
}

.about-subtitle{
  max-width:850px;
  margin:20px auto 0;
  color:#666;
  line-height:1.9;
  font-size:17px;
}

/* =========================
   MAIN CARD
========================= */

.about-card{
  background:rgba(255,255,255,.95);
  backdrop-filter:blur(10px);
  border-radius:30px;
  padding:50px;
  box-shadow:0 20px 60px rgba(0,0,0,.08);
  transition:.35s;
}

.about-card:hover{
  transform:translateY(-8px);
}

.company-heading{
  color:#0B2E7A;
  font-size:34px;
  font-weight:700;
  margin-bottom:20px;
}

.company-text{
  color:#555;
  line-height:2;
  font-size:16px;
  margin-bottom:30px;
}

/* =========================
   FEATURES
========================= */

.features-grid{
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(300px,1fr));
  gap:25px;
  margin-top:40px;
}

.feature-card{
  background:#fff;
  border-radius:20px;
  padding:22px;
  display:flex;
  align-items:center;
  gap:15px;
  box-shadow:0 10px 30px rgba(0,0,0,.08);
  border-left:4px solid #0B2E7A;
  transition:.35s;
}

.feature-card:hover{
  transform:translateY(-10px);
  box-shadow:0 20px 40px rgba(11,46,122,.15);
}

.feature-icon{
  color:#0B2E7A;
  transition:.4s;
  flex-shrink:0;
}

.feature-card:hover .feature-icon{
  transform:rotate(360deg) scale(1.2);
  color:#ff9800;
}

.feature-text{
  color:#333;
  font-weight:600;
  line-height:1.6;
}

/* =========================
   OWNER SECTION
========================= */

.owner-box{
  margin-top:60px;
  background:linear-gradient(135deg,#0B2E7A,#1748b5);
  color:#fff;
  padding:40px;
  border-radius:25px;
  text-align:center;
  box-shadow:0 15px 40px rgba(11,46,122,.25);
  transition:.35s;
}

.owner-box:hover{
  transform:translateY(-8px);
}

.owner-name{
  font-size:28px;
  font-weight:700;
  margin-top:15px;
}

.owner-designation{
  color:#FFD700;
  font-size:18px;
  margin-top:5px;
}

.owner-message{
  max-width:800px;
  margin:20px auto;
  line-height:1.9;
  font-size:16px;
}

/* =========================
   TABLET
========================= */

@media (max-width:992px){

  .about-section{
    padding:80px 20px;
  }

  .about-header{
    margin-bottom:55px;
  }

  .about-title{
    font-size:40px;
  }

  .about-subtitle{
    font-size:16px;
    padding:0 10px;
  }

  .about-card{
    padding:40px 30px;
  }

  .company-heading{
    font-size:30px;
  }

  .features-grid{
    grid-template-columns:repeat(2,1fr);
    gap:20px;
  }

}

/* =========================
   MOBILE
========================= */

@media (max-width:768px){

  .about-section{
    padding:60px 15px;
  }

  .about-header{
    margin-bottom:40px;
  }

  .about-title{
    font-size:32px;
  }

  .about-subtitle{
    font-size:14px;
    line-height:1.8;
  }

  .about-card{
    padding:25px 20px;
    border-radius:20px;
  }

  .company-heading{
    font-size:26px;
    text-align:center;
  }

  .company-text{
    font-size:15px;
    line-height:1.8;
    text-align:justify;
  }

  .features-grid{
    grid-template-columns:1fr;
    gap:18px;
  }

  .feature-card{
    padding:18px;
    gap:12px;
  }

  .feature-icon{
    width:24px;
    height:24px;
  }

  .feature-text{
    font-size:15px;
  }

  .owner-box{
    margin-top:40px;
    padding:30px 20px;
    border-radius:18px;
  }

  .owner-name{
    font-size:24px;
  }

  .owner-designation{
    font-size:16px;
  }

  .owner-message{
    font-size:15px;
    line-height:1.8;
  }

}

/* =========================
   SMALL MOBILE
========================= */

@media (max-width:480px){

  .about-section{
    padding:45px 12px;
  }

  .about-title{
    font-size:28px;
  }

  .about-subtitle{
    font-size:13px;
  }

  .company-heading{
    font-size:22px;
  }

  .company-text{
    font-size:14px;
  }

  .about-card{
    padding:20px 16px;
  }

  .feature-card{
    padding:15px;
  }

  .feature-text{
    font-size:14px;
  }

  .owner-box{
    padding:25px 15px;
  }

  .owner-name{
    font-size:20px;
  }

  .owner-designation{
    font-size:15px;
  }

  .owner-message{
    font-size:14px;
  }

}
`}</style>

      <section className="about-section">
        <div className="about-container">

          {/* Header */}
          <div className="about-header">
            <Building2 size={60} className="about-icon" />

            <h2 className="about-title">
              About Jaina Enterprises
            </h2>

            <p className="about-subtitle">
              A trusted and respected name in Industrial Metal
              Supply, committed to transparency, compliance,
              professionalism and long-term business relationships.
            </p>
          </div>

          {/* Main Card */}
          <div className="about-card">

            <h3 className="company-heading">
              Jaina Enterprises – A Name You Can Trust in Industrial Metal Supply
            </h3>

            <p className="company-text">
              Jaina Enterprises is a trusted and respected name in the
              metal supply industry. We are committed to conducting
              clean, transparent and GST-compliant business
              transactions.
            </p>

            <p className="company-text">
              Our organization is built on integrity, reliability,
              compliance and long-term business relationships. We
              believe in maintaining the highest standards of
              professionalism and ethical business practices in every
              transaction.
            </p>

            {/* Features */}
            <div className="features-grid">

              <div className="feature-card">
                <ShieldCheck size={30} className="feature-icon" />
                <span className="feature-text">
                  100% Industrial Supply Network
                </span>
              </div>

              <div className="feature-card">
                <Building2 size={30} className="feature-icon" />
                <span className="feature-text">
                  Verified Warehouse Address
                </span>
              </div>

              <div className="feature-card">
                <BadgeCheck size={30} className="feature-icon" />
                <span className="feature-text">
                  Complete GST Compliance
                </span>
              </div>

              <div className="feature-card">
                <FileText size={30} className="feature-icon" />
                <span className="feature-text">
                  Timely GST Returns & Tax Payments
                </span>
              </div>

              <div className="feature-card">
                <CheckCircle2 size={30} className="feature-icon" />
                <span className="feature-text">
                  Ethical Business Practices
                </span>
              </div>

              <div className="feature-card">
                <Users size={30} className="feature-icon" />
                <span className="feature-text">
                  Strong Industry Reputation
                </span>
              </div>

              <div className="feature-card">
                <Handshake size={30} className="feature-icon" />
                <span className="feature-text">
                  Long-Term Reliable Relationships
                </span>
              </div>

            </div>

            {/* Owner Section */}
            <div className="owner-box">

              <h3>Best Regards</h3>

              <p className="owner-message">
                As a socially responsible and trustworthy organization,
                we believe in maintaining the highest standards of
                professionalism and integrity in every deal. You can
                trust Jaina Enterprises for genuine, transparent and
                compliant business transactions.
              </p>

              <div className="owner-name">
                Jyoti Jain
              </div>

              <div className="owner-designation">
                Owner – Jaina Enterprises
              </div>

            </div>

          </div>
        </div>
      </section>
      <Gallery />
    </>
  );
};

export default About;